// src/utils/translateText.js
// English -> any language translation helper (Groq API, direct frontend call, backend vagar).
// Emojis are stripped before translating and re-attached afterwards.
//
// .env: VITE_GROQ_API_KEY=gsk_xxxx
// optional: VITE_GROQ_MODEL=openai/gpt-oss-120b
// optional: VITE_GROQ_FALLBACK_MODELS=openai/gpt-oss-20b   (comma separated)

const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu;
const MAX_LENGTH = 500;

const API_KEY = import.meta.env?.VITE_GROQ_API_KEY || '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';
const PRIMARY_MODEL = import.meta.env?.VITE_GROQ_MODEL || 'openai/gpt-oss-120b';

// Primary model fail thay (429/5xx/404) to aa models ek pachi ek try thase
const FALLBACK_MODELS = (import.meta.env?.VITE_GROQ_FALLBACK_MODELS || 'openai/gpt-oss-20b')
    .split(',')
    .map((m) => m.trim())
    .filter((m) => m && m !== PRIMARY_MODEL);

const MODELS = [PRIMARY_MODEL, ...FALLBACK_MODELS];
const API_BASE = 'https://api.groq.com/openai/v1';

const MAX_ATTEMPTS = 4; // overall rounds (darek round ma badha models try thay)
const BASE_DELAY_MS = 1500; // 1.5s, 3s, 6s
const REQUEST_TIMEOUT_MS = 25000;
const DEBUG = true; // false karo to console log band

const LANG_NAMES = {
    en: 'English',
    hi: 'Hindi',
    id: 'Indonesian',
    es: 'Spanish',
    pt: 'Brazilian Portuguese',
    vi: 'Vietnamese',
    gu: 'Gujarati',
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const log = (...args) => {
    if (DEBUG) console.error('[translateText]', ...args);
};

async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

// Groq no error body vanchi ne readable message banave
async function readGroqError(res) {
    let detail = '';
    let code = '';
    try {
        const body = await res.json();
        detail = body?.error?.message || JSON.stringify(body);
        code = body?.error?.code || '';
    } catch {
        detail = await res.text().catch(() => '');
    }
    return { detail: String(detail || '').slice(0, 300), code };
}

// Console ma run karo: await window.listGroqModels()
// Tamari key ne kayā models available che e batave
export async function listGroqModels() {
    if (!API_KEY) throw new Error('VITE_GROQ_API_KEY is not set');

    const res = await fetch(`${API_BASE}/models`, {
        headers: { Authorization: `Bearer ${API_KEY}` },
    });

    if (!res.ok) {
        const { detail } = await readGroqError(res);
        console.error('[translateText] listModels failed', res.status, detail);
        throw new Error(`listModels failed (${res.status}) ${detail}`);
    }

    const data = await res.json();
    const names = (data?.data || []).map((m) => m.id);

    console.log('[translateText] Available models:', names);
    return names;
}

if (typeof window !== 'undefined') {
    window.listGroqModels = listGroqModels;
}

function buildPrompt(text, targets) {
    const langList = targets.map((code) => `"${code}": ${LANG_NAMES[code]}`).join(', ');

    return `Translate the English text below into these languages: ${langList}.
Rules:
- The text is a casual question/statement from a young-people social app. Keep the same tone, slang feel and meaning. Do not translate word-for-word if it sounds unnatural.
- Keep names, @mentions and numbers as they are.
- Return ONLY a JSON object whose keys are the language codes and values are the translations. No extra text.

Text: ${JSON.stringify(text)}`;
}

// Ek model par ek attempt: ek j request ma badhi languages => { hi: '...', id: '...' }
async function requestWithModel(model, text, targets) {
    const url = `${API_BASE}/chat/completions`;

    const body = {
        model,
        temperature: 0.2,
        max_completion_tokens: 2048,
        response_format: { type: 'json_object' },
        messages: [
            {
                role: 'system',
                content:
                    'You are a professional translator. You always reply with a single valid JSON object and nothing else.',
            },
            { role: 'user', content: buildPrompt(text, targets) },
        ],
    };

    // gpt-oss reasoning models chhe, low effort rakhvathi translation fast aave
    if (model.includes('gpt-oss')) {
        body.reasoning_effort = 'low';
    }

    let res;
    try {
        res = await fetchWithTimeout(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${API_KEY}`,
            },
            body: JSON.stringify(body),
        });
    } catch (netErr) {
        // timeout / network error => retry thay
        const err = new Error(
            netErr?.name === 'AbortError' ? 'Translation request timed out' : `Network error: ${netErr.message}`
        );
        err.retryable = true;
        throw err;
    }

    if (!res.ok) {
        const { detail, code } = await readGroqError(res);
        log(`Groq ${res.status} | model: ${model} | code: ${code} | detail: ${detail}`);

        let message;
        if (res.status === 404) {
            message = `Model "${model}" not found. Console ma window.listGroqModels() chalavo. (${detail})`;
        } else if (res.status === 429) {
            message = `Translation limit reached, try again later. (${detail})`;
        } else if (res.status === 401 || res.status === 403) {
            message = `Groq ${res.status}: ${detail} (API key check karo)`;
        } else if (res.status === 413) {
            message = `Groq 413: request too large. (${detail})`;
        } else if (res.status === 400) {
            message = `Groq 400: ${detail}`;
        } else {
            message = `Groq ${res.status}: ${detail}`;
        }

        const err = new Error(message);
        err.status = res.status;
        // 429 (per-model limit) + 5xx => retry / bija model par jao
        err.retryable = res.status === 429 || res.status >= 500;
        // 404 => bija model par jao
        err.skipModel = res.status === 404;
        // JSON generate na thayu (400 json_validate_failed) => retry karva jevu
        if (res.status === 400 && code === 'json_validate_failed') err.retryable = true;
        // Key problem / bad request => turant stop
        err.fatal =
            [401, 403, 413].includes(res.status) || (res.status === 400 && code !== 'json_validate_failed');
        throw err;
    }

    const data = await res.json();
    const raw = data?.choices?.[0]?.message?.content || '';

    if (!raw) {
        log('Empty response from Groq', model, JSON.stringify(data).slice(0, 500));
        const err = new Error('Empty response from Groq');
        err.retryable = true;
        throw err;
    }

    let parsed;
    try {
        parsed = JSON.parse(raw.replace(/```json|```/g, '').trim());
    } catch (e) {
        log('JSON parse failed. Raw response:', raw.slice(0, 500));
        const err = new Error('Groq response JSON nathi (parse failed)');
        err.retryable = true;
        throw err;
    }

    const result = {};
    targets.forEach((code) => {
        const value = parsed?.[code];
        if (typeof value === 'string' && value.trim()) {
            result[code] = value.trim();
        }
    });

    if (Object.keys(result).length === 0) {
        const err = new Error('Groq e koi translation na aapi');
        err.retryable = true;
        throw err;
    }

    return result;
}

// Darek round ma badha models ek pachi ek, round vache backoff
async function requestTranslations(text, langs) {
    if (!API_KEY) {
        throw new Error('VITE_GROQ_API_KEY is not set (.env check karo, dev server restart karo)');
    }

    const targets = langs.filter((code) => LANG_NAMES[code]);
    if (targets.length === 0) throw new Error('No valid target languages');

    let lastError;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        let shouldRetry = false;

        for (const model of MODELS) {
            try {
                return await requestWithModel(model, text, targets);
            } catch (error) {
                lastError = error;
                log(`Round ${attempt}/${MAX_ATTEMPTS} | model ${model} failed:`, error.message);

                // Key / bad request problem => koi pan model ma same j aavse, turant stop
                if (error.fatal) throw error;

                if (error.retryable) shouldRetry = true;
                // 404 (skipModel) ke retryable => next model try karo
            }
        }

        if (!shouldRetry || attempt === MAX_ATTEMPTS) break;

        await sleep(BASE_DELAY_MS * 2 ** (attempt - 1)); // 1.5s, 3s, 6s
    }

    throw lastError || new Error('Translation failed');
}

export async function translateText(text, targetLang) {
    if (!text?.trim()) return '';

    const emojis = text.match(emojiRegex) || [];
    const withoutEmoji = text.replace(emojiRegex, '').trim();

    if (!withoutEmoji) return emojis.join(' ');

    if (withoutEmoji.length > MAX_LENGTH) {
        throw new Error(`Text is too long to translate (max ${MAX_LENGTH} characters)`);
    }

    const translations = await requestTranslations(withoutEmoji, [targetLang]);
    const translated = translations[targetLang];

    if (!translated) throw new Error('Translation failed');

    return (translated + (emojis.length ? ' ' + emojis.join(' ') : '')).trim();
}

// Translates one English text into many languages (1 request).
// Returns { translations: { hi: '...', id: '...' }, failed: ['es'], error?: 'reason' }
export async function translateToAllLanguages(text, targetLangs) {
    const translations = {};
    let failed = [...targetLangs];

    if (!text?.trim()) {
        targetLangs.forEach((lang) => {
            translations[lang] = '';
        });
        return { translations, failed: [] };
    }

    const emojis = text.match(emojiRegex) || [];
    const withoutEmoji = text.replace(emojiRegex, '').trim();

    // Fakt emoji hoy to translate ni jarur nathi
    if (!withoutEmoji) {
        targetLangs.forEach((lang) => {
            translations[lang] = emojis.join(' ');
        });
        return { translations, failed: [] };
    }

    if (withoutEmoji.length > MAX_LENGTH) {
        throw new Error(`Text is too long to translate (max ${MAX_LENGTH} characters)`);
    }

    let errorMessage = null;

    try {
        const result = await requestTranslations(withoutEmoji, targetLangs);

        failed = [];
        targetLangs.forEach((lang) => {
            if (result[lang]) {
                translations[lang] = (
                    result[lang] + (emojis.length ? ' ' + emojis.join(' ') : '')
                ).trim();
            } else {
                failed.push(lang);
            }
        });
    } catch (error) {
        // badhi languages fail => failed ma j rahe, reason return karo (toast ma batavva mate)
        errorMessage = error.message;
    }

    return errorMessage ? { translations, failed, error: errorMessage } : { translations, failed };
}